import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  VStack,
  HStack,
  Text,
  Badge,
  IconButton,
  useDisclosure,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  Flex,
  Avatar,
  useToast,
  Progress,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  FiEye,
  FiEdit,
  FiPlus,
  FiSearch,
  FiBriefcase,
  FiTrendingUp,
  FiCalendar,
  FiStar,
} from "react-icons/fi";
import { digitalFilesService } from "../digitalFilesService";
import JobHistoryModal from "./JobHistoryModal";

const JobHistoryTab = ({
  employees,
  onEmployeeSelect,
  onViewProfile,
  refreshData,
}) => {
  // Dark mode colors
  const mutedColor = useColorModeValue("gray.600", "gray.400");

  const [jobHistory, setJobHistory] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  // Fetch job history for all employees
  const fetchJobHistory = async () => {
    try {
      setLoading(true);
      const response = await digitalFilesService.jobHistory.getAllJobHistory();
      setJobHistory(response.data || []);
    } catch (error) {
      console.error("Error fetching job history:", error);
      toast({
        title: "Error loading job history",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setJobHistory([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobHistory();
  }, []);

  const handleViewJobHistory = async (employee) => {
    try {
      const response = await digitalFilesService.jobHistory.getJobHistory(
        employee.employeeId
      );
      setSelectedEmployee({
        ...employee,
        jobHistory: response.data,
      });
      onOpen();
    } catch (error) {
      toast({
        title: "Error loading job history",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCreateJobHistory = (employee) => {
    setSelectedEmployee(employee);
    onOpen();
  };

  // Filter employees based on search query
  const filteredEmployees = employees.filter((employee) => {
    const employeeJobHistory = jobHistory.filter(
      (jh) => jh.employeeId === employee.employeeId
    );
    const searchString = [
      employee.employeeId,
      employee.name,
      employee.email,
      employeeJobHistory.map((jh) => jh.positionInfo?.jobTitle).join(" "),
      employeeJobHistory.map((jh) => jh.positionInfo?.department).join(" "),
    ]
      .join(" ")
      .toLowerCase();

    return searchString.includes(searchQuery.toLowerCase());
  });

  const getJobHistorySummary = (employee) => {
    const employeeJobHistory = jobHistory.filter(
      (jh) => jh.employeeId === employee.employeeId
    );
    const currentPosition = employeeJobHistory.find(
      (jh) => jh.positionInfo?.isCurrentPosition
    );

    return {
      totalPositions: employeeJobHistory.length,
      currentPosition: currentPosition?.positionInfo?.jobTitle || "Not Set",
      department: currentPosition?.positionInfo?.department || "N/A",
      tenure: employeeJobHistory.length > 0 ? "Active" : "No History",
    };
  };

  const getTenureColor = (tenure) => {
    return tenure === "Active" ? "green" : "gray";
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Text fontSize="xl" fontWeight="bold" color="gray.800">
            Job History & Career Progression
          </Text>
          <Text color={mutedColor}>
            Track employee career paths, promotions, and performance history
          </Text>
        </Box>
        <Button
          leftIcon={<FiPlus />}
          colorScheme="blue"
          onClick={() => {
            setSelectedEmployee(null);
            onOpen();
          }}
        >
          Add Job History
        </Button>
      </Flex>

      {/* Search */}
      <InputGroup maxW="400px" mb={6}>
        <InputLeftElement pointerEvents="none">
          <FiSearch color="gray" />
        </InputLeftElement>
        <Input
          placeholder="Search employees or positions..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </InputGroup>

      {/* Employees Table */}
      <Box
        overflowX="auto"
        borderRadius="lg"
        border="1px"
        borderColor="gray.200"
      >
        <Table variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th>Employee</Th>
              <Th>Current Position</Th>
              <Th>Department</Th>
              <Th>Tenure Status</Th>
              <Th>Total Positions</Th>
              <Th>Performance</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredEmployees.map((employee) => {
              const summary = getJobHistorySummary(employee);
              const employeeJobHistory = jobHistory.filter(
                (jh) => jh.employeeId === employee.employeeId
              );
              const avgRating =
                employeeJobHistory.length > 0
                  ? employeeJobHistory
                      .flatMap(
                        (jh) =>
                          jh.performanceReviews?.map((r) => r.rating) || []
                      )
                      .reduce((sum, rating) => sum + rating, 0) /
                    employeeJobHistory.flatMap(
                      (jh) => jh.performanceReviews || []
                    ).length
                  : 0;

              return (
                <Tr key={employee._id} _hover={{ bg: "gray.50" }}>
                  <Td>
                    <HStack spacing={3}>
                      <Avatar size="sm" name={employee.name} />
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="medium">{employee.name}</Text>
                        <Text fontSize="sm" color={mutedColor}>
                          ID: {employee.employeeId}
                        </Text>
                      </VStack>
                    </HStack>
                  </Td>
                  <Td>
                    <VStack align="start" spacing={0}>
                      <Text fontWeight="medium">{summary.currentPosition}</Text>
                      <Text fontSize="sm" color={mutedColor}>
                        {summary.department}
                      </Text>
                    </VStack>
                  </Td>
                  <Td>{summary.department}</Td>
                  <Td>
                    <Badge colorScheme={getTenureColor(summary.tenure)}>
                      {summary.tenure}
                    </Badge>
                  </Td>
                  <Td>
                    <Text fontSize="lg" fontWeight="bold">
                      {summary.totalPositions}
                    </Text>
                  </Td>
                  <Td>
                    {avgRating > 0 ? (
                      <HStack spacing={2}>
                        <FiStar color="gold" />
                        <Text>{avgRating.toFixed(1)}/5</Text>
                      </HStack>
                    ) : (
                      <Text color={mutedColor}>No reviews</Text>
                    )}
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <IconButton
                        icon={<FiEye />}
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewJobHistory(employee)}
                        aria-label="View job history"
                      />
                      <IconButton
                        icon={<FiPlus />}
                        size="sm"
                        colorScheme="blue"
                        onClick={() => handleCreateJobHistory(employee)}
                        aria-label="Add job history"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onViewProfile(employee)}
                      >
                        Profile
                      </Button>
                    </HStack>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>

      {/* Career Progression Stats */}
      {filteredEmployees.length > 0 && (
        <Box mt={8} p={6} bg="gray.50" borderRadius="lg">
          <Text fontSize="lg" fontWeight="bold" mb={4}>
            Career Progression Overview
          </Text>
          <StatGroup>
            <Stat>
              <StatLabel>Employees with Job History</StatLabel>
              <StatNumber>
                {
                  filteredEmployees.filter((emp) =>
                    jobHistory.some((jh) => jh.employeeId === emp.employeeId)
                  ).length
                }
              </StatNumber>
            </Stat>
            <Stat>
              <StatLabel>Total Position Records</StatLabel>
              <StatNumber>{jobHistory.length}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>Average Positions per Employee</StatLabel>
              <StatNumber>
                {filteredEmployees.length > 0
                  ? (jobHistory.length / filteredEmployees.length).toFixed(1)
                  : 0}
              </StatNumber>
            </Stat>
          </StatGroup>
        </Box>
      )}

      {/* Job History Modal */}
      <JobHistoryModal
        isOpen={isOpen}
        onClose={onClose}
        employee={selectedEmployee}
        onSuccess={() => {
          fetchJobHistory();
          refreshData();
        }}
      />
    </Box>
  );
};

export default JobHistoryTab;
